import * as React from 'react'
import { useMemo, useState } from 'react'

import { Box, List, Section, Text } from '@factorialco/deprecated-design-system'
import { ItAssetsTableDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  safeAssetTranslateType,
  safeAssetTypeAsIcon,
} from 'modules/it_management/components/AssetsList/utils'

interface EditDevicesModalProps {
  open: boolean
  onClose: () => void
  assets: Array<{
    id: string
    serialNumber?: string | null
    itAssetModel?: {
      typeName?: string | null
      brand?: string | null
      name?: string | null
    } | null
  }>
}

const EditDevicesModal: React.FC<EditDevicesModalProps> = ({ open, onClose, assets }) => {
  return (
    <Sidepanel
      overlay={false}
      boxPadding={false}
      width="wide"
      title="Edit devices"
      onClose={onClose}
      open={open}
      headerBorder
      closeLabel={i18n.t('common.close')}
      locked
    >
      <Box flexDirection="column" gap="s24" padding="s24">
        <Box>
          <Text.Header3 color="grey1000">Current devices</Text.Header3>
        </Box>

        <List>
          {assets.map((asset) => (
            <DeviceListItem key={asset.id} asset={asset} />
          ))}
        </List>
      </Box>
    </Sidepanel>
  )
}

interface DeviceListItemProps {
  asset: {
    id: string
    serialNumber?: string | null
    itAssetModel?: {
      typeName?: string | null
      brand?: string | null
      name?: string | null
    } | null
  }
}

const DeviceListItem: React.FC<DeviceListItemProps> = ({ asset }) => {
  if (!asset || !asset.id) {
    return null
  }

  const brand = asset.itAssetModel?.brand || 'Unknown'
  const model = asset.itAssetModel?.name || 'Asset'
  const deviceType = safeAssetTranslateType(asset.itAssetModel?.typeName ?? '')
  const serialNumber = asset.serialNumber

  return (
    <List.Item>
      <Box flexDirection="row" alignItems="center" width="full" gap="s12">
        <Box>
          {React.createElement(safeAssetTypeAsIcon(asset.itAssetModel?.typeName ?? ''), {
            style: { width: 16, height: 16, color: '#374151' },
          })}
        </Box>

        <Box flexGrow>
          <List.Title ellipsis>
            {brand} {model}
          </List.Title>
          <Text size="100" color="grey600">
            {deviceType}
            {serialNumber && ` • ${serialNumber}`}
          </Text>
        </Box>
      </Box>
    </List.Item>
  )
}

interface EmployeeItAssetsProps {
  employeeId: number
}

const EmployeeItAssets: React.FC<EmployeeItAssetsProps> = ({ employeeId }) => {
  const [showEditModal, setShowEditModal] = useState(false)

  const { data, loading, error } = useQuery(ItAssetsTableDocument, {
    variables: {
      ownerIds: [employeeId],
      perPage: 50, // Limit to 50 assets for performance
    },
  })

  const assets = useMemo(() => {
    return data?.itManagement.itAssetsConnection.nodes ?? []
  }, [data])

  // Add safety check for employeeId
  if (!employeeId || typeof employeeId !== 'number') {
    return null
  }

  // Add error handling
  if (error) {
    return null
  }

  if (loading) {
    return <Loading />
  }

  if (assets.length === 0) {
    return null
  }

  return (
    <Section
      illustration="building"
      title="IT Assets"
      description={`${assets.length} asset${assets.length !== 1 ? 's' : ''} assigned`}
    >
      <List>
        {assets.map((asset) => (
          <DeviceListItem key={asset.id} asset={asset} />
        ))}
      </List>

      {showEditModal && (
        <EditDevicesModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          assets={assets}
        />
      )}
    </Section>
  )
}

export default EmployeeItAssets
